/**
 * AWQ and GPTQ: 4-bit weights wearing an int32 costume.
 *
 * ## The failure this exists to fix
 *
 * Reported on the launch post (2026-08-08), with headers read over the same kind
 * of range request this tool uses. `Qwen/Qwen2.5-7B-Instruct-AWQ`, layer 0
 * `mlp.gate_proj`: dtype `I32`, shape `[3584, 2368]`, 33 947 648 bytes. Dtype and
 * shape alone say **32.00 bits per element**. The real figure is **4.16**.
 *
 * The declared shape is already packed eight values to one int32, so the logical
 * matrix is `[3584, 18944]` and `2368 = 18944 / 8`. Every AWQ and GPTQ weight
 * reads as a full int32 block unless something unpacks it — and it fails
 * *quietly*: a plausible number, in the right units, wrong by a factor of eight.
 *
 * ## Why one fixed rule cannot work
 *
 * **The two families pack along different axes.**
 *
 *     AWQ   packs columns:  qweight [3584, 2368]   in 3584, out 18944
 *     GPTQ  packs rows:     qweight [704,  2048]   in 5632, out 2048
 *
 * Same factor of eight, different dimension. A rule that assumes either one gets
 * the other family wrong by that factor, in the direction that looks reasonable.
 *
 * ## What settles it, without reading a single payload byte
 *
 * The `scales` plane. Its last dimension is `out_features` in **both** families,
 * so whichever `qweight` dimension does not match it is the packed one:
 *
 *     AWQ   scales [28, 18944] -> out 18944; qweight dim1 2368 != out, so dim1 is packed
 *     GPTQ  scales [44,  2048] -> out  2048; qweight dim1 2048 == out, so dim0 is packed
 *
 * And the group size falls out of the same tensor: `scales` dim 0 is
 * `in / groupSize` — 3584/28 = 128, 5632/44 = 128.
 *
 * Credit where due: this diagnostic is the reporter's, not ours.
 *
 * ## What the number is *for*
 *
 * Their reframing, which is better than the question we were answering: not what
 * a tensor is stored at, but **how much of a scheme's budget goes to the
 * codebook instead of the weights**. `qweight` alone is exactly 4.000 bits on all
 * four tensors they checked; everything above that is group overhead —
 * 3.8% for AWQ, 4.1% for GPTQ, which carries a little more because `g_idx` adds
 * another int32 per input feature.
 *
 * That is the same shape of fact this tool already reports for MLX (4.50 against
 * a nominal 4.00) and MXFP4 (4.25). This module makes AWQ and GPTQ say it too.
 *
 * Pure: shapes in, arithmetic out, no I/O.
 */

import type { DeclaredQuantization } from "./mlx.js";

/** What a packed-int group needs to describe itself. */
export interface PackedTensorLike {
  readonly name: string;
  readonly dtype: string;
  readonly shape: readonly number[];
  readonly byteLength: number;
}

/** Which axis the payload was packed along — the thing that differs by family. */
export type PackedAxis = "columns" | "rows";

export interface PackedIntGroup {
  /** Shared prefix, e.g. `model.layers.0.mlp.gate_proj`. */
  readonly prefix: string;
  /** Every physical tensor in the group, `qweight` first. */
  readonly partNames: readonly string[];
  readonly axis: PackedAxis;
  readonly inFeatures: number;
  readonly outFeatures: number;
  readonly bits: number;
  readonly groupSize: number;
  /** `inFeatures x outFeatures` — the logical matrix, not the packed one. */
  readonly params: number;
  /** Every byte the scheme spends on this projection, sidecars included. */
  readonly bytes: number;
  /** Total bytes as bits per logical weight — 4.156, not 32.00. */
  readonly effectiveBpw: number;
  /** `qweight` alone, which is the nominal width and always exactly `bits`. */
  readonly payloadBpw: number;
  /** Set when the config and the shapes cannot both be true. Surfaced, never smoothed. */
  readonly disagreement: string | undefined;
}

/**
 * Which output column each nibble of an int32 belongs to, in AWQ.
 *
 * **Measured against ground truth, not read off a spec** — see
 * `og/scripts/verify-awq-order.ts`. `Qwen/Qwen2.5-7B-Instruct-AWQ` is a
 * quantization of `Qwen/Qwen2.5-7B-Instruct`, which ships the same tensor
 * unquantized, so a dequantized slice can be correlated against the original
 * weights:
 *
 *     sequential  [0,1,2,3,4,5,6,7]   r = 0.0108
 *     [0,4,1,5,2,6,3,7]               r = 0.0060
 *     [0,2,4,6,1,3,5,7]               r = 0.9916   <- this one
 *
 * The residual is 4-bit quantization error, which is what should be left over.
 *
 * **This had to be measured, and a plausible answer was wrong.** The order
 * above was the second guess; the widely-cited interleave is its inverse and
 * correlates with nothing. A wrong permutation is undetectable by inspection:
 * every value is still present, each output column still receives its own
 * scale, so the histogram, the mean and the per-column magnitude all look
 * exactly right and only the assignment of weights to positions is scrambled.
 * Any statistical check would have passed it.
 */
const AWQ_NIBBLE_ORDER: readonly number[] = [0, 2, 4, 6, 1, 3, 5, 7];

/** GPTQ lays its values down in order; only AWQ permutes. */
const SEQUENTIAL_ORDER: readonly number[] = [0, 1, 2, 3, 4, 5, 6, 7];

/**
 * The nibble order for a family, by the axis it packs.
 *
 * Keyed on the axis rather than on a family name because the axis is what the
 * shapes tell us — `findPackedIntGroups` never learns which family it is
 * looking at, and does not need to.
 */
export function nibbleOrder(axis: PackedAxis): readonly number[] {
  return axis === "columns" ? AWQ_NIBBLE_ORDER : SEQUENTIAL_ORDER;
}

/** Bit widths these schemes pack into 32-bit words. 7 does not divide 32. */
const PACKED_BITS: readonly number[] = [2, 3, 4, 8];

const BITS_PER_WORD = 32;

/** The sidecars that belong to a `qweight`, in the order they are looked for. */
const SIDECARS = [".scales", ".qzeros", ".g_idx"] as const;

/** What each sidecar is, for a reader who opened one expecting weights. */
const SIDECAR_ROLES: Readonly<Record<string, string>> = {
  ".scales": "the per-group scale plane",
  ".qzeros": "the packed per-group zero points",
  ".g_idx": "the group index, mapping each input feature to a group",
};

/**
 * If this tensor is part of a packed group but not its payload, say what it is.
 *
 * Opening one of these used to report that "decoding this scheme isn't
 * supported yet", which is both wrong and discouraging: the scheme *is*
 * supported, and this particular tensor is a sidecar with no distribution of its
 * own to plot. GPTQ has three integer tensors per module against AWQ's two, so
 * this is markedly easier to hit there — and `g_idx` sorts before `qweight`.
 */
export function describePackedSidecar(
  name: string,
  groups: readonly PackedIntGroup[],
): string | undefined {
  for (const group of groups) {
    for (const suffix of SIDECARS) {
      if (name !== `${group.prefix}${suffix}`) continue;
      const bpw = group.effectiveBpw.toFixed(2);
      return `This is ${SIDECAR_ROLES[suffix]} for ${group.prefix} — not its weights, so there is no distribution to plot. Open ${group.prefix}.qweight for the reconstructed weights. These bytes are already counted in that row's ${bpw} bits per weight.`;
    }
  }
  return undefined;
}

function isIntPayload(dtype: string): boolean {
  return dtype === "I32" || dtype === "U32";
}

/** Product of every dimension but the last — expert stacks are 3-D. */
function leadingProduct(shape: readonly number[]): number {
  return shape.slice(0, -1).reduce((a, b) => a * b, 1);
}

/**
 * Resolve one group's geometry, or explain why it could not be.
 *
 * Declared config first where it exists — AWQ and GPTQ both state `bits` and
 * `group_size`, and a stated fact beats an inferred one. The shapes then have to
 * agree, and where they do not, the disagreement is reported rather than
 * silently preferred either way: a repo whose config and tensors contradict each
 * other is a finding, not a rounding error.
 */
function resolveGeometry(
  qweight: PackedTensorLike,
  scales: PackedTensorLike,
  declared: DeclaredQuantization | undefined,
): Omit<PackedIntGroup, "prefix" | "partNames" | "bytes" | "effectiveBpw"> | undefined {
  const packedShape = qweight.shape;
  const scaleShape = scales.shape;
  if (packedShape.length < 2 || scaleShape.length < 2) return undefined;

  const outFeatures = scaleShape[scaleShape.length - 1] as number;
  const packedLast = packedShape[packedShape.length - 1] as number;
  const packedLead = leadingProduct(packedShape);
  const groupsPerColumn = leadingProduct(scaleShape);
  if (outFeatures <= 0 || packedLast <= 0 || packedLead <= 0) return undefined;

  // The reporter's rule: whichever qweight dim is not `out` is the packed one.
  const axis: PackedAxis = packedLast === outFeatures ? "rows" : "columns";

  const candidates: { bits: number; inFeatures: number }[] = [];
  for (const bits of PACKED_BITS) {
    const perWord = BITS_PER_WORD / bits;
    if (axis === "columns") {
      // qweight [in, out/perWord]
      if (packedLast * perWord !== outFeatures) continue;
      candidates.push({ bits, inFeatures: packedLead });
    } else {
      // qweight [in/perWord, out]
      candidates.push({ bits, inFeatures: packedLead * perWord });
    }
  }
  if (candidates.length === 0) return undefined;

  const declaredBits = declared?.bits;
  const chosen =
    (declaredBits !== undefined && candidates.find((c) => c.bits === declaredBits)) ||
    (candidates.length === 1 ? candidates[0] : undefined);
  if (!chosen) return undefined;

  const { bits, inFeatures } = chosen;
  if (inFeatures <= 0 || groupsPerColumn <= 0) return undefined;
  if (inFeatures % groupsPerColumn !== 0) return undefined;
  const groupSize = inFeatures / groupsPerColumn;

  // A declaration that contradicts the shapes is reported, and the shapes win —
  // they are the bytes that exist, where the config is a claim about them.
  const parts: string[] = [];
  if (declaredBits !== undefined && declaredBits !== bits) {
    parts.push(`config declares ${declaredBits}-bit but the packing implies ${bits}`);
  }
  if (declared?.groupSize !== undefined && declared.groupSize !== groupSize) {
    parts.push(
      `config declares group ${declared.groupSize} but scales imply ${groupSize}`,
    );
  }
  /*
   * The family, cross-checked against the axis rather than inferred from it.
   *
   * `dequantizePackedInt` encodes "rows-packing implies GPTQ" as
   * `zeroOffset = axis === "rows" ? 1 : 0` — GPTQ stores `zero - 1`, AWQ stores
   * the zero point. That is a *family* fact derived from a *layout* observable,
   * and it happens to hold across every repo measured. A rows-packing AWQ
   * variant would silently get GPTQ's zero handling, which shifts every weight
   * by one quantization step: a plausible distribution, uniformly wrong.
   *
   * We already parse `quant_method`, so the two can be compared instead of one
   * standing in for the other. Reported through the same channel as a bits or
   * group-size contradiction rather than resolved here — the decoder's rule is
   * unchanged and the reader is told the config disagrees with it. Review
   * finding, 2026-08-09.
   */
  const family = (declared?.method ?? "").toLowerCase();
  const impliedFamily = axis === "rows" ? "gptq" : "awq";
  const namesOther =
    (impliedFamily === "gptq" && family.includes("awq")) ||
    (impliedFamily === "awq" && family.includes("gptq"));
  if (namesOther) {
    parts.push(
      `config declares ${family.includes("awq") ? "AWQ" : "GPTQ"} but the ${axis}-packing implies ${impliedFamily.toUpperCase()}, which decides the zero-point convention`,
    );
  }

  return {
    axis,
    inFeatures,
    outFeatures,
    bits,
    groupSize,
    params: inFeatures * outFeatures,
    payloadBpw: (qweight.byteLength * 8) / (inFeatures * outFeatures),
    disagreement: parts.length > 0 ? parts.join("; ") : undefined,
  };
}

/**
 * Find every AWQ/GPTQ group among these tensors.
 *
 * Keyed on `qweight` rather than on `scales`: `scales` alone is ambiguous — MLX
 * uses that name too — while `qweight` is specific to the packed-int families.
 */
export function findPackedIntGroups(
  tensors: readonly PackedTensorLike[],
  declared?: DeclaredQuantization,
): PackedIntGroup[] {
  const byName = new Map<string, PackedTensorLike>();
  for (const t of tensors) byName.set(t.name, t);

  const groups: PackedIntGroup[] = [];
  for (const qweight of tensors) {
    if (!qweight.name.endsWith(".qweight")) continue;
    if (!isIntPayload(qweight.dtype)) continue;
    const prefix = qweight.name.slice(0, -".qweight".length);

    const scales = byName.get(`${prefix}.scales`);
    if (!scales) continue;

    const geometry = resolveGeometry(qweight, scales, declared);
    if (!geometry) continue;

    const partNames = [qweight.name];
    let bytes = qweight.byteLength;
    for (const suffix of SIDECARS) {
      const part = byName.get(`${prefix}${suffix}`);
      if (!part) continue;
      partNames.push(part.name);
      bytes += part.byteLength;
    }

    groups.push({
      prefix,
      partNames,
      ...geometry,
      bytes,
      effectiveBpw: (bytes * 8) / geometry.params,
    });
  }
  return groups;
}

/** Everything a packed-int slice needs to become floats. */
export interface PackedDequantInput {
  /** The packed payload rows, as read. */
  readonly packed: Uint32Array;
  /** `qzeros` for every group the slice touches — the whole plane. */
  readonly qzeros: Uint32Array;
  /** `scales` for every group, `[groups, outFeatures]`, already decoded. */
  readonly scales: Float32Array;
  /**
   * `g_idx`, when the repo ships one.
   *
   * Not optional decoration: GPTQ's act-order permutes which group a feature
   * belongs to, so `f / groupSize` is simply wrong there. Measured on
   * `TinyLlama-1.1B-Chat-v0.3-GPTQ`, assuming the arithmetic scored 0.9267
   * against ground truth where `g_idx` scored 0.9911.
   */
  readonly gIdx: Int32Array | undefined;
  readonly bits: number;
  readonly groupSize: number;
  readonly axis: PackedAxis;
  readonly outFeatures: number;
  /** Index of the first *input feature* this slice covers. */
  readonly firstFeature: number;
  /** Input features in the slice. */
  readonly features: number;
}

/**
 * Reconstruct the weights a packed-int slice stands for.
 *
 * Every constant here was measured against ground truth rather than recalled —
 * see `og/scripts/verify-{awq,gptq}-order.ts`. Four facts, and each one is
 * invisible if wrong:
 *
 * | | AWQ | GPTQ |
 * | --- | --- | --- |
 * | payload axis | columns | rows |
 * | nibble order | `[0,2,4,6,1,3,5,7]` | sequential |
 * | zero point | as stored | **stored + 1** |
 * | group lookup | `f / groupSize` | `g_idx[f]` |
 *
 * `qzeros` packs the **output** axis in both families, even where `qweight`
 * packs the input one. A dequantizer using one layout for both reads the zero
 * points as noise.
 */
export function dequantizePackedInt(input: PackedDequantInput): Float32Array {
  const { bits, groupSize, axis, outFeatures, features, firstFeature } = input;
  if (bits !== 4) {
    throw new Error(
      `Packed-int dequantization is verified for 4-bit only; got ${bits}. Widening it needs the same ground-truth check the 4-bit path had, because a wrong unpacking is indistinguishable from a right one by inspection.`,
    );
  }
  const perWord = BITS_PER_WORD / bits;
  /*
   * The rows-axis branch computes its nibble from the *slice-local* row `r`,
   * which is only the right nibble when the slice starts on a word boundary.
   *
   * That invariant holds today — `tensor-fetch.ts` builds its runs from whole
   * packed words — but it holds by construction elsewhere, and nothing here
   * said so. A future caller passing an unaligned slice would get silently
   * scrambled weights: still the right *shape*, still a plausible distribution,
   * and wrong in a way no statistical check catches. Review finding,
   * 2026-08-09; asserted in the function that relies on it rather than trusted
   * from the one that maintains it.
   */
  if (axis === "rows" && firstFeature % perWord !== 0) {
    throw new Error(
      `Packed rows-axis slices must start on a word boundary: firstFeature ${firstFeature} is not a multiple of ${perWord}. An unaligned slice decodes to the wrong nibbles, which looks like plausible weights.`,
    );
  }
  const order = nibbleOrder(axis);
  // GPTQ stores `zero - 1`; AWQ stores the zero point itself.
  const zeroOffset = axis === "rows" ? 1 : 0;
  const zerosPerRow = Math.ceil(outFeatures / perWord);

  const out = new Float32Array(features * outFeatures);
  const packedCols =
    axis === "columns" ? Math.ceil(outFeatures / perWord) : outFeatures;

  for (let r = 0; r < features; r += 1) {
    const feature = firstFeature + r;
    const group = input.gIdx
      ? (input.gIdx[feature] as number)
      : Math.floor(feature / groupSize);
    const scaleBase = group * outFeatures;
    const zeroBase = group * zerosPerRow;

    for (let c = 0; c < outFeatures; c += 1) {
      let q: number;
      if (axis === "columns") {
        // qweight [features, out/perWord]; nibble n holds column w*perWord + order[n].
        const w = Math.floor(c / perWord);
        const n = order.indexOf(c - w * perWord);
        q = ((input.packed[r * packedCols + w] as number) >>> (n * bits)) & 0xf;
      } else {
        // qweight [features/perWord, out]; nibble n holds feature R*perWord + n.
        const R = Math.floor(r / perWord);
        const n = r - R * perWord;
        q = ((input.packed[R * packedCols + c] as number) >>> (n * bits)) & 0xf;
      }
      /*
       * `qzeros` packs the output axis in both families — but with the *same
       * nibble order as the payload*, not sequentially.
       *
       * Getting this wrong is exactly what a comment asserting the opposite
       * cost: `verify-packed-decode.ts` scored the shipped function at
       * r = 0.0115 on AWQ while the inline arithmetic it was derived from
       * scored 0.9916. GPTQ passed throughout, because its order is the
       * identity and the two readings coincide — so the bug was invisible in
       * one family and total in the other.
       */
      const zw = Math.floor(c / perWord);
      const zn = order.indexOf(c - zw * perWord);
      const z =
        (((input.qzeros[zeroBase + zw] as number) >>> (zn * bits)) & 0xf) + zeroOffset;
      out[r * outFeatures + c] = (q - z) * (input.scales[scaleBase + c] as number);
    }
  }
  return out;
}
